import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Ticket, Copy, Check, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { formatCOP } from '@/utils/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kindLabel = {
  welcome: 'Bienvenida',
  referral: 'Por invitar a un amigo',
  next_purchase: 'Regalo por tu compra',
};

const daysLeft = (iso) => Math.ceil((new Date(iso) - Date.now()) / 86400000);

const CouponCard = ({ coupon }) => {
  const [copied, setCopied] = useState(false);
  const active = coupon.status === 'active';
  const left = coupon.expiry_date ? daysLeft(coupon.expiry_date) : null;
  const urgent = active && left !== null && left <= 5;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      toast.success('Cupón copiado');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <div
      className={`rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
        active ? 'bg-white border-dashed border-[#2A5C43]' : 'bg-[#FAFAF9] border-[#E7E5E4] opacity-70'
      }`}
      data-testid={`coupon-${coupon.code}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8BA58F] font-semibold">
          {kindLabel[coupon.kind] || 'Cupón personal'}
        </p>
        <p className="font-display font-bold text-xl text-[#1C1917] tracking-wider mt-0.5 break-all">{coupon.code}</p>
        <p className="text-sm text-[#2A5C43] font-semibold mt-1">
          {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% de descuento` : `${formatCOP(coupon.discount_value)} de descuento`}
          {coupon.min_purchase > 0 && <span className="text-[#57534E] font-normal"> · compras desde {formatCOP(coupon.min_purchase)}</span>}
        </p>
        {coupon.expiry_date && (
          <p className={`flex items-center gap-1 text-xs mt-2 ${urgent ? 'text-[#B45309] font-semibold' : 'text-[#57534E]'}`}>
            <Clock className="w-3 h-3" />
            {active
              ? `Vence el ${new Date(coupon.expiry_date).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}${left !== null ? ` (${left} ${left === 1 ? 'día' : 'días'})` : ''}`
              : coupon.status === 'used' ? 'Ya utilizado' : coupon.status === 'expired' ? 'Vencido' : 'Inactivo'}
          </p>
        )}
      </div>
      {active && (
        <button
          onClick={copy}
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#2A5C43] hover:bg-[#214A35] text-white text-sm font-semibold px-4 py-2 transition-colors"
          data-testid={`copy-coupon-${coupon.code}`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      )}
    </div>
  );
};

export const MyCoupons = () => {
  const [coupons, setCoupons] = useState(null);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    axios.get(`${API}/coupons/mine`, { withCredentials: true })
      .then(({ data }) => setCoupons(data))
      .catch(() => setCoupons([]));
  }, []);

  if (coupons === null) return <div className="h-24 bg-white rounded-2xl border border-[#E7E5E4] animate-pulse" />;

  const active = coupons.filter((c) => c.status === 'active');
  const past = coupons.filter((c) => c.status !== 'active');

  return (
    <section className="mb-12" data-testid="my-coupons-section">
      <div className="flex items-center gap-2 mb-4">
        <Ticket className="w-5 h-5 text-[#D47A4A]" />
        <h2 className="font-display font-semibold text-lg text-[#1C1917]">Mis cupones</h2>
        {active.length > 0 && (
          <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#D47A4A]/10 text-[#D47A4A] px-2 py-0.5 rounded-full" data-testid="active-coupons-count">
            {active.length} {active.length === 1 ? 'vigente' : 'vigentes'}
          </span>
        )}
      </div>
      {active.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 text-sm text-[#57534E]" data-testid="no-active-coupons">
          No tienes cupones vigentes por ahora. Invita a un amigo con tu código y gana un 5% extra.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {active.map((c) => <CouponCard key={c.code} coupon={c} />)}
        </div>
      )}
      {past.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowPast((v) => !v)}
            className="text-xs text-[#57534E] underline underline-offset-2"
            data-testid="toggle-past-coupons"
          >
            {showPast ? 'Ocultar' : 'Ver'} cupones usados o vencidos ({past.length})
          </button>
          {showPast && (
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {past.map((c) => <CouponCard key={c.code} coupon={c} />)}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
